import React, { useState, useEffect, useMemo } from 'react';
import { fehlertext } from '../lib/fehlertext';
import { verfuegbareJahre, monatsEinordnung } from '../lib/utils';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { assignmentsAPI, timesheetsAPI } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import {
  Clock,
  FileText,
  CheckCircle2,
  AlertCircle,
  XCircle,
  ChevronRight,
  ChevronLeft,
  CornerUpLeft,
  Loader2,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';

const monthNames = [
  'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
  'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'
];

// Der gewaehlte Monat wird gemerkt (Auftrag 05): er darf NIE von selbst umspringen,
// auch nicht ueber den Monatswechsel hinweg. Am 1. will fast jeder noch den Vormonat.
const MONAT_SCHLUESSEL = 'timenyzer.dashboard.monat';

const gemerkterMonat = () => {
  try {
    const roh = JSON.parse(localStorage.getItem(MONAT_SCHLUESSEL) || 'null');
    const jahre = verfuegbareJahre();
    if (roh && roh.monat >= 1 && roh.monat <= 12 && jahre.includes(roh.jahr)) return roh;
  } catch (_) { /* kaputter Eintrag -> heutiger Monat */ }
  return null;
};

const EmployeeDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [assignments, setAssignments] = useState([]);
  const [timesheets, setTimesheets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(() => gemerkterMonat()?.monat ?? new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(() => gemerkterMonat()?.jahr ?? new Date().getFullYear());
  const [creatingTimesheet, setCreatingTimesheet] = useState(null);
  const [monatBestaetigung, setMonatBestaetigung] = useState(null);

  useEffect(() => {
    try {
      localStorage.setItem(MONAT_SCHLUESSEL, JSON.stringify({ monat: selectedMonth, jahr: selectedYear }));
    } catch (_) { /* privater Modus: dann eben nicht merken */ }
  }, [selectedMonth, selectedYear]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [assignmentsRes, timesheetsRes] = await Promise.all([
        assignmentsAPI.getAll(),
        timesheetsAPI.getAll(),
      ]);
      setAssignments(assignmentsRes.data);
      setTimesheets(timesheetsRes.data);
    } catch (error) {
      toast.error('Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  };

  const getTimesheetForMonth = (assignmentId, month, year) => {
    return timesheets.find(
      t => t.assignment_id === assignmentId && t.month === month && t.year === year
    );
  };

  // Archivierte Zuordnung nur zeigen, solange im gewählten Monat ein noch nicht
  // eingereichter (offener) Stundenzettel existiert, damit der Monat fertig eingereicht werden kann.
  const visibleAssignments = useMemo(() => {
    return assignments.filter((a) => {
      if (a.is_active !== false) return true;
      const ts = getTimesheetForMonth(a.id, selectedMonth, selectedYear);
      return ts && ts.status === 'offen';
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignments, timesheets, selectedMonth, selectedYear]);

  const calculateTotalHours = (timesheet) => {
    if (!timesheet || !timesheet.entries) return 0;
    // Gesamtstunden wie im PDF und auf der Bestätigungsseite: 'gearbeitet' UND
    // 'betreutes_kind_krank' (das Jugendamt zahlt diese Tage).
    return timesheet.entries.reduce((sum, entry) => {
      if (entry.status === 'gearbeitet' || entry.status === 'betreutes_kind_krank') {
        return sum + (entry.hours || 0);
      }
      return sum;
    }, 0);
  };

  const anlegen = async (assignmentId) => {
    try {
      setCreatingTimesheet(assignmentId);
      const response = await timesheetsAPI.create({
        assignment_id: assignmentId,
        month: selectedMonth,
        year: selectedYear,
      });
      setTimesheets([...timesheets, response.data]);
      navigate(`/timesheet/${response.data.id}`);
    } catch (error) {
      toast.error(fehlertext(error, 'Fehler beim Erstellen'));
    } finally {
      setCreatingTimesheet(null);
    }
  };

  // Nur beim tatsaechlichen ANLEGEN fragen, und nur wenn der Monat weder der heutige
  // noch der Vormonat ist. Ein Dialog, der jeden Tag kommt, wird ungelesen weggeklickt.
  const handleCreateTimesheet = async (assignmentId) => {
    if (einordnung === 'sonstiges') {
      setMonatBestaetigung(assignmentId);
      return;
    }
    await anlegen(assignmentId);
  };

  const getStatusInfo = (timesheet) => {
    if (!timesheet) {
      return {
        status: 'missing',
        message: (<>Du hast deinen Stundenzettel für{' '}
          <span translate="no">{monthNames[selectedMonth - 1]} {selectedYear}</span> noch nicht erstellt.</>),
        color: 'text-red-600',
        bgColor: 'bg-red-50 border-red-200',
        icon: XCircle,
        iconColor: 'text-red-500',
      };
    }
    if (timesheet.status === 'eingereicht') {
      return {
        status: 'submitted',
        message: (<>Du hast deinen Stundenzettel für{' '}
          <span translate="no">{monthNames[selectedMonth - 1]} {selectedYear}</span> eingereicht.</>),
        color: 'text-green-600',
        bgColor: 'bg-green-50 border-green-200',
        icon: CheckCircle2,
        iconColor: 'text-green-500',
      };
    }
    return {
      status: 'pending',
      message: (<>Dein Stundenzettel für{' '}
        <span translate="no">{monthNames[selectedMonth - 1]} {selectedYear}</span> ist noch in Bearbeitung.</>),
      color: 'text-amber-600',
      bgColor: 'bg-amber-50 border-amber-200',
      icon: AlertCircle,
      iconColor: 'text-amber-500',
    };
  };

  const heute = new Date();
  const einordnung = monatsEinordnung(selectedMonth, selectedYear, heute);
  const jahre = verfuegbareJahre(heute);
  const monatsWert = selectedYear * 12 + (selectedMonth - 1);
  const kannZurueck = monatsWert > jahre[0] * 12;
  const kannVor = monatsWert < jahre[jahre.length - 1] * 12 + 11;

  const blaettere = (schritte) => {
    const wert = monatsWert + schritte;
    const jahr = Math.floor(wert / 12);
    if (!jahre.includes(jahr)) return;
    setSelectedYear(jahr);
    setSelectedMonth((wert % 12) + 1);
  };

  const zumAktuellenMonat = () => {
    setSelectedYear(heute.getFullYear());
    setSelectedMonth(heute.getMonth() + 1);
  };

  const einordnungsZeile = {
    aktuell: { text: 'Aktueller Monat', klasse: 'bg-slate-100 text-slate-500' },
    vormonat: { text: 'Vormonat – hier tragen Sie nach', klasse: 'bg-blue-50 text-blue-700' },
    sonstiges: {
      text: (<>Achtung: Sie sind im Monat{' '}
        <span translate="no">{monthNames[selectedMonth - 1]} {selectedYear}</span></>),
      klasse: 'bg-amber-50 text-amber-700 border border-amber-200',
    },
  }[einordnung];

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-48" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="employee-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 font-[Manrope]">
            Hallo, {user?.first_name}!
          </h1>
          <p className="text-slate-500 mt-1">
            Willkommen bei TimeNYzer
          </p>
        </div>
        
        <div className="flex flex-col items-stretch sm:items-end gap-2" data-testid="monats-navigation">
          {/* Monatswechsel ist eine bewusste Handlung: grosse Pfeilknoepfe statt
              Auswahlliste - auf dem Handy sprang die Liste beim Scrollen um. */}
          <div className="flex items-center justify-between sm:justify-end gap-2">
            <Button
              variant="outline"
              className="h-11 w-11 p-0"
              onClick={() => blaettere(-1)}
              disabled={!kannZurueck}
              aria-label="Vorheriger Monat"
              data-testid="monat-zurueck"
            >
              <ChevronLeft className="h-5 w-5" />
            </Button>
            <span
              className="min-w-[11rem] text-center text-base md:text-lg font-semibold text-slate-900"
              data-testid="monat-anzeige"
              translate="no"
            >
              {monthNames[selectedMonth - 1]} {selectedYear}
            </span>
            <Button
              variant="outline"
              className="h-11 w-11 p-0"
              onClick={() => blaettere(1)}
              disabled={!kannVor}
              aria-label="Nächster Monat"
              data-testid="monat-vor"
            >
              <ChevronRight className="h-5 w-5" />
            </Button>
          </div>

          <div className={`text-xs rounded-lg px-2 py-1 text-center ${einordnungsZeile.klasse}`} data-testid="monat-einordnung">
            {einordnungsZeile.text}
          </div>

          {einordnung !== 'aktuell' && (
            <Button
              variant="ghost"
              size="sm"
              className="h-11 text-blue-700 hover:text-blue-800"
              onClick={zumAktuellenMonat}
              data-testid="zum-aktuellen-monat"
            >
              <CornerUpLeft className="h-4 w-4 mr-1" />
              Zurück zu <span translate="no">{monthNames[heute.getMonth()]} {heute.getFullYear()}</span>
            </Button>
          )}
        </div>
      </div>

      {/* Assignments */}
      {visibleAssignments.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <FileText className="h-12 w-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-900 mb-2">
              Keine Zuordnung vorhanden
            </h3>
            <p className="text-slate-500">
              Sie haben noch keine aktive Zuordnung zu einem Kind.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {visibleAssignments.map((assignment) => {
            const timesheet = getTimesheetForMonth(assignment.id, selectedMonth, selectedYear);
            const totalHours = calculateTotalHours(timesheet);
            const statusInfo = getStatusInfo(timesheet);
            const StatusIcon = statusInfo.icon;

            return (
              <Card key={assignment.id} className="overflow-hidden">
                <CardContent className="p-0">
                  {/* Assignment Info Header */}
                  <div className="p-4 sm:p-6 border-b border-slate-100">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                      <div>
                        <h3 className="text-lg font-semibold text-slate-900">
                          {assignment.child_name}
                        </h3>
                        <p className="text-slate-500">{assignment.school_name}</p>
                      </div>
                      
                      {/* Hours Display - Only Sum */}
                      <div className="flex items-center gap-3">
                        <div className="flex items-center gap-2 bg-blue-50 px-4 py-2 rounded-lg">
                          <Clock className="h-5 w-5 text-blue-600" />
                          <div>
                            <span className="text-2xl font-bold text-blue-700" translate="no">{totalHours.toFixed(1)}</span>
                            <span className="text-blue-600 text-sm ml-1">Stunden</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Status Message */}
                  <div className={`p-4 sm:p-6 border-b ${statusInfo.bgColor}`}>
                    <div className="flex items-center gap-3">
                      <StatusIcon className={`h-6 w-6 ${statusInfo.iconColor} flex-shrink-0`} />
                      <p className={`font-medium ${statusInfo.color}`}>
                        {statusInfo.message}
                      </p>
                    </div>
                  </div>

                  {/* Action */}
                  <div className="p-4 sm:p-6 bg-slate-50">
                    {timesheet ? (
                      <Button
                        className="w-full sm:w-auto bg-blue-800 hover:bg-blue-900"
                        onClick={() => navigate(`/timesheet/${timesheet.id}`)}
                        data-testid={`open-timesheet-${assignment.id}`}
                      >
                        <FileText className="h-4 w-4 mr-2" />
                        Stundenzettel öffnen
                        <ChevronRight className="h-4 w-4 ml-2" />
                      </Button>
                    ) : (
                      <Button
                        className="w-full sm:w-auto bg-blue-800 hover:bg-blue-900"
                        onClick={() => handleCreateTimesheet(assignment.id)}
                        disabled={creatingTimesheet === assignment.id}
                        data-testid={`create-timesheet-${assignment.id}`}
                      >
                        {creatingTimesheet === assignment.id ? (
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                          <FileText className="h-4 w-4 mr-2" />
                        )}
                        Stundenzettel erstellen
                        <ChevronRight className="h-4 w-4 ml-2" />
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <AlertDialog open={!!monatBestaetigung} onOpenChange={(o) => !o && setMonatBestaetigung(null)}>
        <AlertDialogContent data-testid="monat-bestaetigung-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Befinden Sie sich im richtigen Monat?</AlertDialogTitle>
            <AlertDialogDescription>
              Sie legen einen Stundenzettel für <strong translate="no">{monthNames[selectedMonth - 1]} {selectedYear}</strong> an.
              Heute ist der <span translate="no">{heute.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })}</span>.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="monat-bestaetigung-abbrechen">Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => { const id = monatBestaetigung; setMonatBestaetigung(null); anlegen(id); }}
              data-testid="monat-bestaetigung-ok"
            >
              Ja, <span translate="no">{monthNames[selectedMonth - 1]} {selectedYear}</span>
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default EmployeeDashboard;
